import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  TextField,
  Chip,
  Divider,
  Avatar
} from '@mui/material';
import {
  ViewList as ViewListIcon,
  Security as SecurityIcon,
  BarChart as BarChartIcon,
  IntegrationInstructions as IntegrationIcon
} from '@mui/icons-material';
import { usePageTitle } from '../hooks/usePageTitle';

const LandingPage: React.FC = () => {
  console.log('🏠 LandingPage component mounted');
  usePageTitle('E-Unify 🇺🇸 Data Intel Platform', false); // false = use exact title
  console.log('📄 Current document.title:', document.title);
  
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [subscribeLoading, setSubscribeLoading] = useState(false);
  const [subscribeMsg, setSubscribeMsg] = useState<string | null>(null);
  const [subscribeErr, setSubscribeErr] = useState<string | null>(null);

  const handleSubscribe = async () => {
    setSubscribeMsg(null);
    setSubscribeErr(null);
    const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    if (!valid) {
      setSubscribeErr('Please enter a valid email address.');
      return;
    }
    try {
      setSubscribeLoading(true);
      const { subscriptionService } = await import('../services/subscriptionService');
      await subscriptionService.subscribe({ email, source: 'landing' });
      setSubscribeMsg('Subscribed! You will receive updates to your inbox.');
      setEmail('');
    } catch (e: any) {
      const msg = e?.response?.data?.error || 'Subscription failed. Please try again later.';
      setSubscribeErr(msg);
    } finally {
      setSubscribeLoading(false);
    }
  };

  const handleRequestDemo = () => {
    navigate('/register');
  };

  const handleSignIn = () => {
    navigate('/login');
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#ffffff' }}>
      {/* DHS-Style Navigation Header */}
      <Box sx={{ 
        bgcolor: 'white', 
        borderBottom: '1px solid #e0e0e0',
        position: 'sticky',
        top: 0,
        zIndex: 1000,
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <Container maxWidth="lg">
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            py: 2
          }}>
            {/* Logo/Brand */}
            <Box sx={{ cursor: 'pointer' }} onClick={() => navigate('/')}>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: '#003366', 
                  fontWeight: 700,
                  '&:hover': { color: '#0071BC' },
                  lineHeight: 1.2
                }}
              >
                E-Unify
              </Typography>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: '#666', 
                  fontSize: '0.6rem',
                  display: 'block',
                  mt: -0.5
                }}
              >
                Enterprise Systems Unification for DHS
              </Typography>
            </Box>

            {/* Navigation Menu */}
            <Box sx={{ 
              display: { xs: 'none', md: 'flex' }, 
              gap: 4,
              alignItems: 'center'
            }}>
              <Typography 
                sx={{ 
                  color: '#003366', 
                  cursor: 'pointer',
                  fontSize: '0.95rem',
                  fontWeight: 500,
                  '&:hover': { color: '#0071BC', textDecoration: 'underline' }
                }}
                onClick={() => navigate('/features')}
              >
                Features
              </Typography>
              <Typography 
                sx={{ 
                  color: '#003366', 
                  cursor: 'pointer',
                  fontSize: '0.95rem',
                  fontWeight: 500,
                  '&:hover': { color: '#0071BC', textDecoration: 'underline' }
                }}
                onClick={() => navigate('/about')}
              >
                About
              </Typography>
              <Typography 
                sx={{ 
                  color: '#003366', 
                  cursor: 'pointer',
                  fontSize: '0.95rem',
                  fontWeight: 500,
                  '&:hover': { color: '#0071BC', textDecoration: 'underline' }
                }}
                onClick={() => navigate('/contact')}
              >
                Contact
              </Typography>
              <Typography 
                sx={{ 
                  color: '#003366', 
                  cursor: 'pointer',
                  fontSize: '0.95rem',
                  fontWeight: 500,
                  '&:hover': { color: '#0071BC', textDecoration: 'underline' }
                }}
                onClick={() => window.open('/media/', '_blank')}
              >
                Media
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={handleSignIn}
                sx={{
                  borderColor: '#003366',
                  color: '#003366',
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  '&:hover': {
                    borderColor: '#0071BC',
                    bgcolor: 'rgba(0,113,188,0.04)'
                  }
                }}
              >
                Sign In
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={handleRequestDemo}
                sx={{
                  bgcolor: '#003366',
                  color: 'white',
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  '&:hover': {
                    bgcolor: '#0071BC'
                  }
                }}
              >
                Get Started
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Hero Section */}
      <Box sx={{ 
        background: 'linear-gradient(135deg, #003366 0%, #004080 50%, #003366 100%)',
        color: 'white', 
        py: { xs: 8, md: 12 },
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.05) 0%, transparent 50%)',
          pointerEvents: 'none'
        }
      }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={7}>
              <Box sx={{
                bgcolor: 'white',
                py: 2,
                px: 3,
                borderRadius: 2,
                mb: 3,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
              }}>
                <Box
                  component="img"
                  src="/images/eunify-m.png"
                  alt="E-Unify Logo"
                  sx={{
                    width: '100%',
                    maxWidth: '380px',
                    height: 'auto',
                    display: 'block'
                  }}
                />
              </Box>
              <Typography variant="h2" sx={{ 
                fontSize: { xs: '1.35rem', md: '1.75rem' }, 
                fontWeight: 700, 
                mb: 1, 
                color: 'white',
                lineHeight: 1.1,
                letterSpacing: '-0.02em'
              }}>
                Enterprise Systems Unification for DHS
              </Typography>
              <Typography variant="h3" sx={{ 
                fontSize: { xs: '1.15rem', md: '1.5rem' }, 
                fontWeight: 400, 
                mb: 3, 
                color: 'rgba(255,255,255,0.9)',
                lineHeight: 1.3
              }}>
                The Data Intelligence Platform Built for Government
              </Typography>
              <Typography variant="body1" sx={{ 
                fontSize: { xs: '1rem', md: '1.15rem' }, 
                mb: 3, 
                color: 'rgba(255,255,255,0.95)',
                lineHeight: 1.5,
                maxWidth: '650px'
              }}>
                Unify data, governance, and AI across federal agencies with FedRAMP-ready infrastructure
              </Typography>

              {/* Stats Grid */}
              <Grid container spacing={2} sx={{ mb: 4, alignItems: 'stretch' }}>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ 
                    bgcolor: 'rgba(255,255,255,0.1)', 
                    backdropFilter: 'blur(10px)',
                    p: 2, 
                    borderRadius: 2,
                    border: '1px solid rgba(255,255,255,0.2)',
                    transition: 'all 0.3s ease',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    minHeight: '100px',
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.15)',
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.2)'
                    }
                  }}>
                    <Typography variant="h3" sx={{ fontSize: '2rem', fontWeight: 700, mb: 0.5, color: 'white', textAlign: 'center' }}>25+</Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.9)', fontWeight: 500, textAlign: 'center', lineHeight: 1.2 }}>Years of Service</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ 
                    bgcolor: 'rgba(255,255,255,0.1)', 
                    backdropFilter: 'blur(10px)',
                    p: 2, 
                    borderRadius: 2,
                    border: '1px solid rgba(255,255,255,0.2)',
                    transition: 'all 0.3s ease',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    minHeight: '100px',
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.15)',
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.2)'
                    }
                  }}>
                    <Typography variant="h3" sx={{ fontSize: '2rem', fontWeight: 700, mb: 0.5, color: 'white', textAlign: 'center' }}>10+</Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.9)', fontWeight: 500, textAlign: 'center', lineHeight: 1.2 }}>Federal Agencies</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ 
                    bgcolor: 'rgba(255,255,255,0.1)', 
                    backdropFilter: 'blur(10px)',
                    p: 2, 
                    borderRadius: 2,
                    border: '1px solid rgba(255,255,255,0.2)',
                    transition: 'all 0.3s ease',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    minHeight: '100px',
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.15)',
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.2)'
                    }
                  }}>
                    <Typography variant="h3" sx={{ fontSize: '2rem', fontWeight: 700, mb: 0.5, color: 'white', textAlign: 'center' }}>3</Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.9)', fontWeight: 500, textAlign: 'center', lineHeight: 1.2 }}>Major Depts<br />(DOD, DHS, DOT)</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ 
                    bgcolor: 'rgba(255,255,255,0.1)', 
                    backdropFilter: 'blur(10px)',
                    p: 2, 
                    borderRadius: 2,
                    border: '1px solid rgba(255,255,255,0.2)',
                    transition: 'all 0.3s ease',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    minHeight: '100px',
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.15)',
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.2)'
                    }
                  }}>
                    <Typography variant="h3" sx={{ fontSize: '2rem', fontWeight: 700, mb: 0.5, color: 'white', textAlign: 'center' }}>100%</Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.9)', fontWeight: 500, textAlign: 'center', lineHeight: 1.2 }}>Government<br />Focus</Typography>
                  </Box>
                </Grid>
              </Grid>

              {/* Buttons */}
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', justifyContent: 'center' }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleRequestDemo}
                  sx={{
                    bgcolor: 'white',
                    color: '#003366',
                    px: 4,
                    py: 1.5,
                    fontSize: '1rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: 3,
                    boxShadow: '0 4px 14px rgba(0,0,0,0.25)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      bgcolor: '#f8f9fa',
                      boxShadow: '0 6px 20px rgba(0,0,0,0.3)',
                      transform: 'translateY(-2px)'
                    }
                  }}
                >
                  Request a Demo →
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={handleSignIn}
                  sx={{
                    borderColor: 'rgba(255,255,255,0.8)',
                    borderWidth: 2,
                    color: 'white',
                    px: 4,
                    py: 1.5,
                    fontSize: '1rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: 3,
                    backdropFilter: 'blur(10px)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: 'white',
                      borderWidth: 2,
                      bgcolor: 'rgba(255, 255, 255, 0.15)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 14px rgba(0,0,0,0.2)'
                    }
                  }}
                >
                  Sign In
                </Button>
              </Box>
            </Grid>

            <Grid item xs={12} md={5} sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'center', alignItems: 'center' }}>
              <Box
                component="img"
                src="/images/government-network.png"
                alt="Federal Agency Network - ICE, USCIS, FEMA, CBP connected through secure data infrastructure"
                sx={{
                  width: '100%',
                  maxWidth: 450,
                  height: 'auto',
                  borderRadius: 3,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                  transition: 'transform 0.3s ease',
                  '&:hover': {
                    transform: 'scale(1.02)'
                  }
                }}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Russell Brian Riehl Bio Section */}
      <Box sx={{ bgcolor: '#f5f5f5', py: 8 }}>
        <Container maxWidth="lg">
          <Card sx={{ p: 4, borderLeft: '6px solid #003366', boxShadow: 2 }}>
            <Typography variant="h3" gutterBottom sx={{ fontWeight: 700, color: '#003366', mb: 3, fontSize: { xs: '1.75rem', md: '2.5rem' }, lineHeight: 1.3 }}>
              E‑Unify: An Original Concept by{' '}
              <Box component="span" sx={{ whiteSpace: 'nowrap' }}>Russell Brian Riehl</Box>
            </Typography>
            <Grid container spacing={4}>
              <Grid item xs={12} md={8}>
              <Typography variant="body1" paragraph sx={{ lineHeight: 1.8, fontSize: '1.05rem' }}>
                <strong>E‑Unify</strong>, an original concept by Russell Brian Riehl, redefines enterprise data unification.
              </Typography>
              <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
                Russell Brian Riehl began his IT career in June 2000 developing <strong>Enterprise Data Warehouses for Fairfax County Public Schools</strong> and has since dedicated more than two decades to advancing technology solutions for government agencies. Guided by the principles of <strong>servant leadership</strong>, he has worked tirelessly to align mission, technology, and data strategy in service of the public good.
              </Typography>
              <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
                His extensive federal experience spans key departments and agencies — including the <strong>Department of Transportation (DOT)</strong>, <strong>Department of Defense (DoD)</strong>, and <strong>Department of Homeland Security (DHS)</strong> — where he supported programs within the FAA, FEMA, FHWA, U.S. Courts, ICE, USCIS, and DISA.
              </Typography>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Over the years, Riehl has built a reputation as a trusted leader who delivers innovation through collaboration and vision through execution, establishing the foundation for what would evolve into <strong>E‑Unify</strong> — a platform built to unify data, governance, and strategy across the enterprise.
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ p: 3, bgcolor: '#e3f2fd', borderRadius: 2, textAlign: 'center' }}>
                <Typography variant="h6" gutterBottom sx={{ color: '#003366', fontWeight: 600 }}>
                  Russell Brian Riehl
                </Typography>
                <Typography variant="subtitle2" gutterBottom sx={{ color: '#B31B1B', fontWeight: 600 }}>
                  Founder & Chief Architect
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ textAlign: 'left' }}>
                  <Typography variant="body2" paragraph><strong>Career Start:</strong> June 2000</Typography>
                  <Typography variant="body2" paragraph><strong>Experience:</strong> 25+ Years</Typography>
                  <Typography variant="body2" paragraph><strong>Focus:</strong> Government IT Solutions</Typography>
                  <Typography variant="body2" paragraph><strong>Leadership Philosophy:</strong> Transformational and Service-Oriented</Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    href="https://www.linkedin.com/in/russell-riehl-724b1a283/"
                    target="_blank"
                    sx={{
                      mt: 2,
                      mb: 2,
                      borderColor: '#003366',
                      color: '#003366',
                      '&:hover': { borderColor: '#003366', bgcolor: 'rgba(0, 51, 102, 0.04)' }
                    }}
                  >
                    View LinkedIn Profile
                  </Button>
                  <Box sx={{ bgcolor: '#fff4e6', p: 2, borderRadius: 1, mt: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#003366', mb: 1 }}>Federal Experience:</Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>• DOT • DoD • DHS</Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>• FAA • FEMA • FHWA</Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>• ICE • USCIS • DISA</Typography>
                    <Typography variant="body2">• U.S. Courts</Typography>
                  </Box>
                </Box>
              </Card>
            </Grid>
          </Grid>
          </Card>
        </Container>
      </Box>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h3" align="center" gutterBottom sx={{ fontWeight: 700, color: '#003366', mb: 2 }}>
          Powerful Features for Enterprise Data Management
        </Typography>
        <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 6 }}>
          Everything you need to govern, catalog, and unify your organization's data assets
        </Typography>
        <Grid container spacing={4}>
          {[
            { icon: <ViewListIcon sx={{ fontSize: 40 }} />, color: '#003366', title: 'Federal Data Discovery', subtitle: 'Data Catalog', desc: 'FISMA-compliant metadata management with automated security classification and cross-agency data sharing capabilities', link: '/features/data-catalog' },
            { icon: <SecurityIcon sx={{ fontSize: 40 }} />, color: '#B31B1B', title: 'Mission-Aligned Governance', subtitle: 'Data Governance', desc: 'NIST 800-53 policy templates, ATO documentation automation, and continuous compliance monitoring for federal agencies', link: '/features/data-governance' },
            { icon: <BarChartIcon sx={{ fontSize: 40 }} />, color: '#003366', title: 'Intelligence-Grade Analytics', subtitle: 'Analytics & Insights', desc: 'Real-time threat detection dashboards, emergency response coordination, and cross-agency intelligence sharing', link: '/features/analytics' },
            { icon: <IntegrationIcon sx={{ fontSize: 40 }} />, color: '#B31B1B', title: 'Secure Government Integration', subtitle: 'Enterprise Integration', desc: 'FedRAMP-authorized connectors, air-gapped deployment options, and DISA STIG compliance for mission-critical systems', link: '/features/integration' }
          ].map((feature, idx) => (
            <Grid item xs={12} sm={6} md={3} key={idx}>
              <Card 
                sx={{ 
                  height: '100%', 
                  p: 3, 
                  textAlign: 'center', 
                  border: '1px solid #e0e0e0',
                  borderTop: feature.color === '#B31B1B' ? '3px solid #B31B1B' : '3px solid #003366',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  '&:hover': { 
                    boxShadow: feature.color === '#B31B1B' ? '0 8px 24px rgba(179,27,27,0.2)' : '0 8px 24px rgba(0,51,102,0.2)', 
                    transform: 'translateY(-4px)',
                    borderTop: feature.color === '#B31B1B' ? '3px solid #B31B1B' : '3px solid #0071BC'
                  } 
                }}
                onClick={() => navigate(feature.link)}
                role="button"
                tabIndex={0}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    navigate(feature.link);
                  }
                }}
                aria-label={`Learn more about ${feature.title}`}
              >
                <Avatar sx={{ width: 80, height: 80, bgcolor: feature.color, mx: 'auto', mb: 2 }}>
                  {feature.icon}
                </Avatar>
                <Typography variant="h6" gutterBottom sx={{ color: '#003366', fontWeight: 600 }}>{feature.title}</Typography>
                <Typography variant="subtitle2" gutterBottom sx={{ color: '#666', fontWeight: 600 }}>{feature.subtitle}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>{feature.desc}</Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* E-Unify Layered Architecture Section */}
      <Box sx={{ bgcolor: '#f5f5f5', py: 8 }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            align="center"
            gutterBottom
            sx={{ fontWeight: 700, color: '#003366', mb: 2 }}
          >
            E-Unify Layered Architecture
          </Typography>
          <Typography
            variant="body1"
            align="center"
            color="text.secondary"
            sx={{ mb: 3, maxWidth: 900, mx: 'auto' }}
          >
            E-Unify is a modular, open-source enterprise data unification platform. Each layer in the
            architecture maps to a major feature area, helping agencies see how trusted, AI-enabled data
            products are delivered from infrastructure up to mission outcomes.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => navigate('/admin/documentation')}
              sx={{
                borderColor: '#003366',
                color: '#003366',
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': {
                  borderColor: '#003366',
                  bgcolor: 'rgba(0, 51, 102, 0.04)'
                }
              }}
            >
              View full architecture in Documentation Center
            </Button>
          </Box>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Card sx={{ p: 3, height: '100%' }}>
                <Typography variant="overline" sx={{ color: '#B31B1B', fontWeight: 700 }}>
                  Data / AI Products Layer
                </Typography>
                <Typography variant="h6" gutterBottom sx={{ color: '#003366', fontWeight: 600 }}>
                  Prebuilt and Custom Data/AI Products
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                  E-Unify delivers both prebuilt and customizable Data/AI products. Agencies can deploy
                  ready-made data catalogs, dashboards, workflow modules, and automation tools—or extend the
                  platform with bespoke AI-powered analytics tailored to mission-specific workflows.
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card sx={{ p: 3, height: '100%' }}>
                <Typography variant="overline" sx={{ color: '#003366', fontWeight: 700 }}>
                  Ontology Layer
                </Typography>
                <Typography variant="h6" gutterBottom sx={{ color: '#003366', fontWeight: 600 }}>
                  Unified Semantic and Governance Layer
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                  At the heart of E-Unify is the Ontology Layer, a unified semantic layer that translates
                  complex technical data into business-friendly, government-compliant terminology. It
                  supports traceability, interoperability, and consistent definitions for all data assets
                  through a unified business glossary and semantic governance.
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ p: 3, height: '100%' }}>
                <Typography variant="overline" sx={{ color: '#003366', fontWeight: 700 }}>
                  Services Layer
                </Typography>
                <Typography variant="h6" gutterBottom sx={{ color: '#003366', fontWeight: 600 }}>
                  Data Services
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                  Metadata management, cataloging, lineage tracing, and automated quality checks powered by
                  open standards such as DataHub and extensible through custom data connectors to source
                  systems.
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ p: 3, height: '100%' }}>
                <Typography variant="overline" sx={{ color: '#003366', fontWeight: 700 }}>
                  Services Layer
                </Typography>
                <Typography variant="h6" gutterBottom sx={{ color: '#003366', fontWeight: 600 }}>
                  AI Services
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                  Operationalized machine learning models, anomaly detection, and predictive analytics that
                  are governed to comply with federal standards and can be tuned for specific agency missions
                  and portfolios.
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ p: 3, height: '100%' }}>
                <Typography variant="overline" sx={{ color: '#003366', fontWeight: 700 }}>
                  Services Layer
                </Typography>
                <Typography variant="h6" gutterBottom sx={{ color: '#003366', fontWeight: 600 }}>
                  Workflow Services
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                  Visual BPMN workflow design, policy enforcement, and orchestration that integrates with
                  DevSecOps, program management, and compliance monitoring—ensuring policies are executable,
                  not just documented.
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card sx={{ p: 3, height: '100%' }}>
                <Typography variant="overline" sx={{ color: '#003366', fontWeight: 700 }}>
                  Security & Governance Layer
                </Typography>
                <Typography variant="h6" gutterBottom sx={{ color: '#003366', fontWeight: 600 }}>
                  Governance-First Security and Compliance
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                  The governance layer enforces policies for security, privacy, and compliance (FedRAMP,
                  FISMA, CMMC), including access control and auditability. Agencies can manage risk,
                  monitor stewardship, and maintain the highest standards for sensitive information.
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card sx={{ p: 3, height: '100%' }}>
                <Typography variant="overline" sx={{ color: '#003366', fontWeight: 700 }}>
                  Software Delivery Layer
                </Typography>
                <Typography variant="h6" gutterBottom sx={{ color: '#003366', fontWeight: 600 }}>
                  DevSecOps, Automation, and Accessibility
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                  The software delivery foundation supports CI/CD, open-source deployment, Section 508
                  accessibility, and infrastructure automation with tools like Ansible and Kubernetes. This
                  enables rapid, repeatable deployment across cloud-native, multi-tenant, and hybrid agency
                  environments.
                </Typography>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Trusted by Government Section */}
      <Box sx={{ bgcolor: '#f5f5f5', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" align="center" gutterBottom sx={{ fontWeight: 700, color: '#003366', mb: 2 }}>
            Trusted by Government Agencies
          </Typography>
          <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 6 }}>
            25+ years delivering mission-critical data solutions to federal departments and agencies
          </Typography>
          <Grid container spacing={3} sx={{ mb: 6 }}>
            {[
              { num: '25+', label: 'Years of Service', sub: 'Since June 2000' },
              { num: '3', label: 'Major Departments', sub: 'DOT, DoD, DHS' },
              { num: '10+', label: 'Federal Agencies', sub: 'FAA, FEMA, ICE, USCIS' },
              { num: '100%', label: 'Government Focus', sub: 'Built for federal missions' }
            ].map((stat, idx) => (
              <Grid item xs={12} sm={6} md={3} key={idx}>
                <Card sx={{ p: 3, textAlign: 'center', bgcolor: 'white' }}>
                  <Typography variant="h2" sx={{ color: '#003366', fontWeight: 700 }}>{stat.num}</Typography>
                  <Typography variant="h6" gutterBottom sx={{ color: '#003366' }}>{stat.label}</Typography>
                  <Typography variant="body2" color="text.secondary">{stat.sub}</Typography>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, color: '#003366', mt: 6, mb: 3 }}>
            Federal Agency Experience
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Card sx={{ 
                p: 3, 
                height: '100%',
                borderTop: '4px solid #003366',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 12px 32px rgba(0,51,102,0.2)',
                  borderTopColor: '#0071BC'
                }
              }}>
                <Typography variant="h6" gutterBottom sx={{ color: '#003366', fontWeight: 600 }}>Department of Homeland Security</Typography>
                <List dense>
                  <ListItem><ListItemText primary="U.S. Immigration and Customs Enforcement (ICE)" /></ListItem>
                  <ListItem><ListItemText primary="U.S. Citizenship and Immigration Services (USCIS)" /></ListItem>
                  <ListItem><ListItemText primary="Federal Emergency Management Agency (FEMA)" /></ListItem>
                </List>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ 
                p: 3, 
                height: '100%',
                borderTop: '4px solid #B31B1B',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 12px 32px rgba(179,27,27,0.2)',
                  borderTopColor: '#D32F2F'
                }
              }}>
                <Typography variant="h6" gutterBottom sx={{ color: '#003366', fontWeight: 600 }}>Department of Transportation</Typography>
                <List dense>
                  <ListItem><ListItemText primary="Federal Aviation Administration (FAA)" /></ListItem>
                  <ListItem><ListItemText primary="Federal Highway Administration (FHWA)" /></ListItem>
                </List>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ 
                p: 3, 
                height: '100%',
                borderTop: '4px solid #003366',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 12px 32px rgba(0,51,102,0.2)',
                  borderTopColor: '#0071BC'
                }
              }}>
                <Typography variant="h6" gutterBottom sx={{ color: '#003366', fontWeight: 600 }}>Other Agencies</Typography>
                <List dense>
                  <ListItem><ListItemText primary="Department of Defense (DoD)" /></ListItem>
                  <ListItem><ListItemText primary="• Defense Information Systems Agency (DISA)" sx={{ pl: 2 }} /></ListItem>
                  <ListItem><ListItemText primary="U.S. Courts" /></ListItem>
                  <ListItem><ListItemText primary="Fairfax County Public Schools" /></ListItem>
                </List>
              </Card>
            </Grid>
          </Grid>

          <Card sx={{ p: 4, mt: 3, bgcolor: '#e3f2fd', textAlign: 'center', borderLeft: '6px solid #003366' }}>
            <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 600, fontSize: { xs: '1.1rem', md: '1.25rem' }, mb: 2 }}>
              Government-Specific Expertise
            </Typography>
            <Typography variant="body1" sx={{ fontSize: '0.95rem', lineHeight: 1.8, maxWidth: '900px', mx: 'auto' }}>
              E-Unify isn't adapted from commercial software—it's <strong>built from the ground up</strong> for government missions. With deep experience across DHS, DOT, and DoD, we understand federal data challenges, compliance requirements, and the unique needs of mission-critical systems.
            </Typography>
          </Card>
        </Container>
      </Box>

      {/* Why E-Unify Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h3" align="center" gutterBottom sx={{ fontWeight: 700, color: '#003366', mb: 2 }}>
          Why E-Unify?
        </Typography>
        <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 6 }}>
          Purpose-built for government, not adapted from commercial software
        </Typography>
        <Grid container spacing={4}>
          {[
            { title: 'Built for Government from Day One', desc: 'E-Unify was designed specifically for federal missions—not a commercial product retrofitted for government use. Every feature reflects deep understanding of federal data challenges, compliance requirements, and mission-critical operations.' },
            { title: 'CMMC Level 2 Ready Architecture', desc: 'E-Unify implements 54% of NIST SP 800-171 controls with MFA, account lockout, password security, and comprehensive audit logging. Deploy in your secure environment and include E-Unify in your CMMC assessment—just like Linux, PostgreSQL, and other open-source tools used in government.', link: '/security' },
            { title: '508 Compliant by Design', desc: 'Accessibility isn\'t an add-on—it\'s fundamental to E-Unify\'s architecture. Full Section 508 compliance and WCAG 2.0 Level AA ensure all users can access and utilize the platform, meeting federal accessibility mandates from the start.' },
            { title: 'FedRAMP-Ready Architecture', desc: 'Designed with FedRAMP authorization in mind, E-Unify uses government-approved cloud services, implements NIST 800-53 controls, and supports continuous ATO processes. Migration path to AWS GovCloud and MongoDB Atlas for Government ensures compliance scalability.' },
            { title: '25+ Years Federal IT Experience', desc: 'Founded by Russell Brian Riehl, who has served DOT, DoD, and DHS since 2000, E-Unify brings unmatched government IT expertise. We understand federal procurement, security clearances, interagency collaboration, and the unique challenges of mission-critical systems.' },
            { title: 'Servant Leadership Philosophy', desc: 'E-Unify operates on servant leadership principles—putting mission success and public good above profit. We\'re committed to supporting federal agencies in their critical work, not maximizing shareholder returns. Your mission is our mission.' },
            { title: 'No Vendor Lock-In', desc: 'Built on open standards and API-first architecture, E-Unify ensures you maintain control of your data and systems. Export capabilities, standard protocols, and transparent documentation mean you\'re never trapped in a proprietary ecosystem.' }
          ].map((item, idx) => (
            <Grid item xs={12} md={6} key={idx}>
              <Card 
                onClick={() => item.link && navigate(item.link)}
                sx={{ 
                  p: 3, 
                  height: '100%',
                  border: '1px solid #e0e0e0',
                  borderLeft: idx % 2 === 0 ? '4px solid #003366' : '4px solid #B31B1B',
                  transition: 'all 0.3s ease',
                  cursor: item.link ? 'pointer' : 'default',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: idx % 2 === 0 
                      ? '0 12px 32px rgba(0,51,102,0.2)' 
                      : '0 12px 32px rgba(179,27,27,0.2)',
                    borderLeft: idx % 2 === 0 ? '4px solid #0071BC' : '4px solid #B31B1B',
                    bgcolor: idx % 2 === 0 ? 'rgba(0,113,188,0.02)' : 'rgba(179,27,27,0.02)'
                  }
                }}
              >
                <Typography variant="h6" gutterBottom sx={{ 
                  color: '#003366', 
                  fontWeight: 600,
                  transition: 'color 0.3s ease'
                }}>
                  {item.title}
                  {item.link && (
                    <Typography component="span" sx={{ ml: 1, color: '#0071BC', fontSize: '0.9rem' }}>
                      →
                    </Typography>
                  )}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                  {item.desc}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
        <Card sx={{ 
          p: 4, 
          mt: 4, 
          bgcolor: '#003366', 
          color: 'white', 
          textAlign: 'center',
          borderTop: '4px solid #B31B1B',
          borderLeft: '4px solid #B31B1B',
          boxShadow: '0 8px 24px rgba(179,27,27,0.2)'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 2 }}>
            <SecurityIcon sx={{ color: '#B31B1B', fontSize: '2rem' }} />
            <Typography variant="h5" sx={{ fontWeight: 600, color: 'white' }}>
              The E-Unify Difference
            </Typography>
            <SecurityIcon sx={{ color: '#B31B1B', fontSize: '2rem' }} />
          </Box>
          <Typography variant="body1" sx={{ color: 'white', lineHeight: 1.6 }}>
            While commercial data platforms serve Fortune 500 companies, E-Unify serves the nation. We speak your language, understand your constraints, and deliver solutions that work within federal IT ecosystems—not against them.
          </Typography>
        </Card>
      </Container>

      {/* What's New Section */}
      <Box sx={{ bgcolor: '#f5f5f5', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" align="center" gutterBottom sx={{ fontWeight: 700, color: '#003366', mb: 2 }}>
            What's New at E-Unify
          </Typography>
          <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 6 }}>
            Latest updates and developments in government data intelligence
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Card sx={{ p: 3, height: '100%' }}>
                <Chip label="OCTOBER 2025" size="small" sx={{ mb: 2, bgcolor: '#003366', color: 'white' }} />
                <Typography variant="overline" color="text.secondary">Latest Release</Typography>
                <Typography variant="h6" gutterBottom sx={{ color: '#003366', fontWeight: 600 }}>
                  E-Unify v2.0: Enhanced AI Governance
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  New AI use case management, model governance, and compliance tracking capabilities aligned with DHS CIO priorities for responsible AI deployment across federal agencies.
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ p: 3, height: '100%' }}>
                <Chip label="OCTOBER 2025" size="small" sx={{ mb: 2, bgcolor: '#003366', color: 'white' }} />
                <Typography variant="overline" color="text.secondary">Strategic Update</Typography>
                <Typography variant="h6" gutterBottom sx={{ color: '#003366', fontWeight: 600 }}>
                  Aligning with DHS 2025 Priorities
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Platform updates supporting CIO Antoine McCord's AI modernization and cybersecurity initiatives, including enhanced integration with CBP and ICE data systems.
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ p: 3, height: '100%' }}>
                <Chip label="IN PROGRESS" size="small" sx={{ mb: 2, bgcolor: '#B31B1B', color: 'white' }} />
                <Typography variant="overline" color="text.secondary">Compliance</Typography>
                <Typography variant="h6" gutterBottom sx={{ color: '#003366', fontWeight: 600 }}>
                  FedRAMP Authorization Path
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Formal FedRAMP Moderate authorization process initiated with AWS GovCloud migration and MongoDB Atlas for Government deployment planned for Q4 2025.
                </Typography>
              </Card>
            </Grid>
          </Grid>
          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#003366', fontWeight: 600 }}>Coming Soon</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Card sx={{ p: 2 }}>
                  <Typography variant="subtitle2" color="primary">Q4 2025</Typography>
                  <Typography variant="body2">MongoDB Atlas for Government Migration</Typography>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card sx={{ p: 2 }}>
                  <Typography variant="subtitle2" color="primary">Q4 2025</Typography>
                  <Typography variant="body2">Enhanced 508 Compliance Testing Suite</Typography>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card sx={{ p: 2 }}>
                  <Typography variant="subtitle2" color="primary">Q1 2026</Typography>
                  <Typography variant="body2">Government Data Marketplace</Typography>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </Container>
      </Box>

      {/* DHS Technology Leadership Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h3" align="center" gutterBottom sx={{ fontWeight: 700, color: '#003366', mb: 2 }}>
          DHS Technology & Research Leadership
        </Typography>
        <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 6 }}>
          Leading innovation in cybersecurity, AI, and homeland security technology
        </Typography>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 4, height: '100%', borderTop: '4px solid #003366', boxShadow: 2 }}>
              <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 600, fontSize: '1.5rem' }}>
                Antoine McCord
              </Typography>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: '#B31B1B', fontSize: '1rem' }}>
                Chief Information Officer (CIO)
              </Typography>
              <Typography variant="body2" gutterBottom sx={{ fontStyle: 'italic', color: '#666', mb: 2 }}>
                Appointed March 2025
              </Typography>
              <Typography variant="body2" paragraph>
                Antoine McCord oversees DHS's enterprise-wide <strong>$11 billion IT budget</strong>, the largest of any federal agency, managing cybersecurity, digital modernization, and artificial intelligence initiatives.
              </Typography>
              <Typography variant="body2" paragraph>
                With 18+ years of experience in the U.S. Marine Corps and Intelligence Community, McCord specializes in cyber operations, threat detection, and technology integration.
              </Typography>
              <Box sx={{ bgcolor: '#e3f2fd', p: 2, borderRadius: 1, mt: 3 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: '#003366' }}>Key Priorities:</Typography>
                <List dense sx={{ pl: 2 }}>
                  <ListItem sx={{ display: 'list-item', listStyleType: 'disc', pl: 0 }}>
                    <ListItemText primary="Expanding AI and data modernization across CBP and ICE" primaryTypographyProps={{ variant: 'body2' }} />
                  </ListItem>
                  <ListItem sx={{ display: 'list-item', listStyleType: 'disc', pl: 0 }}>
                    <ListItemText primary="Implementing enterprise cybersecurity and modernizing major systems" primaryTypographyProps={{ variant: 'body2' }} />
                  </ListItem>
                  <ListItem sx={{ display: 'list-item', listStyleType: 'disc', pl: 0 }}>
                    <ListItemText primary="Aligning IT operations with national security objectives" primaryTypographyProps={{ variant: 'body2' }} />
                  </ListItem>
                </List>
              </Box>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 4, height: '100%', borderTop: '4px solid #B31B1B', boxShadow: 2 }}>
              <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 600, fontSize: '1.5rem' }}>
                Dr. Dimitri Kusnezov
              </Typography>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: '#B31B1B', fontSize: '1rem' }}>
                Under Secretary for Science & Technology
              </Typography>
              <Typography variant="body2" gutterBottom sx={{ fontStyle: 'italic', color: '#666', mb: 2 }}>
                Science and Technology Directorate (S&T)
              </Typography>
              <Typography variant="body2" paragraph>
                The Science and Technology Directorate serves as DHS's research and development arm, providing scientific and technical expertise to address homeland security threats.
              </Typography>
              <Typography variant="body2" paragraph>
                In 2025, S&T underwent major reorganization to focus on mission-aligned research supporting border security, immigration enforcement, and countering transnational criminal organizations.
              </Typography>
              <Box sx={{ bgcolor: '#fff4e6', p: 2, borderRadius: 1, mt: 3 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: '#003366' }}>Research Focus Areas:</Typography>
                <List dense sx={{ pl: 2 }}>
                  <ListItem sx={{ display: 'list-item', listStyleType: 'disc', pl: 0 }}>
                    <ListItemText primary="Threat characterization: chemical, biological, explosive, and AI threats" primaryTypographyProps={{ variant: 'body2' }} />
                  </ListItem>
                  <ListItem sx={{ display: 'list-item', listStyleType: 'disc', pl: 0 }}>
                    <ListItemText primary="Cybersecurity and critical infrastructure protection" primaryTypographyProps={{ variant: 'body2' }} />
                  </ListItem>
                  <ListItem sx={{ display: 'list-item', listStyleType: 'disc', pl: 0 }}>
                    <ListItemText primary="Emergency response and public safety technologies" primaryTypographyProps={{ variant: 'body2' }} />
                  </ListItem>
                </List>
              </Box>
            </Card>
          </Grid>
        </Grid>

        <Card sx={{ p: 4, mt: 4, bgcolor: '#e3f2fd' }}>
          <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 600 }}>
            How E-Unify Supports DHS Technology Leadership
          </Typography>
          <Typography variant="body1" paragraph>
            E-Unify directly aligns with DHS's 2025 technology priorities, providing the enterprise data governance and integration platform needed to support CIO McCord's AI modernization initiatives and the S&T Directorate's research mission.
          </Typography>
          <Grid container spacing={3} sx={{ mt: 2 }}>
            <Grid item xs={12} md={4}>
              <Typography variant="h6" gutterBottom sx={{ color: '#003366', fontWeight: 600 }}>AI & Data Modernization</Typography>
              <Typography variant="body2">
                E-Unify's data catalog and governance capabilities enable AI-driven insights across DHS components, supporting McCord's mandate for enterprise-wide modernization.
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="h6" gutterBottom sx={{ color: '#003366', fontWeight: 600 }}>Enterprise Cybersecurity</Typography>
              <Typography variant="body2">
                Secure data integration and compliance tracking align with DHS's focus on cybersecurity and critical infrastructure protection.
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="h6" gutterBottom sx={{ color: '#003366', fontWeight: 600 }}>Research & Innovation</Typography>
              <Typography variant="body2">
                Analytics and real-time coordination support S&T's mission for threat characterization and emergency response technologies.
              </Typography>
            </Grid>
          </Grid>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 3, fontStyle: 'italic' }}>
            2025 DHS Technology Landscape: Under McCord and Kusnezov, DHS reflects a strong shift toward mission-aligned AI, cybersecurity modernization, and operational technology research.
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
            Sources: FedScoop • Nextgov • CDO Magazine • Future of Life Institute
          </Typography>
        </Card>
      </Container>

      {/* Security & Compliance Section */}
      <Box sx={{ bgcolor: '#f5f5f5', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" align="center" gutterBottom sx={{ fontWeight: 700, color: '#003366', mb: 2 }}>
            Security & Compliance
          </Typography>
          <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 6 }}>
            Built with enterprise-grade security and government compliance standards
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ p: 3, textAlign: 'center', height: '100%' }}>
                <Typography variant="h6" gutterBottom sx={{ color: '#003366', fontWeight: 600 }}>508 Compliant</Typography>
                <Typography variant="body2" color="text.secondary">
                  Full Section 508 compliance ensuring accessibility for all users
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ p: 3, textAlign: 'center', height: '100%' }}>
                <Typography variant="h6" gutterBottom sx={{ color: '#003366', fontWeight: 600 }}>WCAG 2.0 AA</Typography>
                <Typography variant="body2" color="text.secondary">
                  Web Content Accessibility Guidelines Level AA certified
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ p: 3, textAlign: 'center', height: '100%' }}>
                <Typography variant="h6" gutterBottom sx={{ color: '#003366', fontWeight: 600 }}>Enterprise Encryption</Typography>
                <Typography variant="body2" color="text.secondary">
                  End-to-end encryption for data at rest and in transit
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ p: 3, textAlign: 'center', height: '100%' }}>
                <Typography variant="h6" gutterBottom sx={{ color: '#003366', fontWeight: 600 }}>Cloud-Native</Typography>
                <Typography variant="body2" color="text.secondary">
                  Scalable, resilient architecture with FedRAMP-ready components
                </Typography>
              </Card>
            </Grid>
          </Grid>

          <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 600, mt: 6, mb: 3 }}>
            Additional Security Features
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Card sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ color: '#003366', fontWeight: 600 }}>Continuous Monitoring</Typography>
                <Typography variant="body2" color="text.secondary">
                  24/7 security monitoring and automated vulnerability scanning
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ color: '#003366', fontWeight: 600 }}>Role-Based Access</Typography>
                <Typography variant="body2" color="text.secondary">
                  Granular access controls and audit trails for compliance
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ color: '#003366', fontWeight: 600 }}>Data Sovereignty</Typography>
                <Typography variant="body2" color="text.secondary">
                  US-based infrastructure with government-grade security
                </Typography>
              </Card>
            </Grid>
          </Grid>

          <Card sx={{ p: 3, mt: 4, bgcolor: '#e3f2fd' }}>
            <Typography variant="body2" color="text.secondary">
              E-Unify is designed with FedRAMP-ready architecture and government compliance standards. Contact us for detailed security documentation and compliance certifications.
            </Typography>
          </Card>
        </Container>
      </Box>

      {/* Resources Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h3" align="center" gutterBottom sx={{ fontWeight: 700, color: '#003366', mb: 2 }}>
          Resources & Guides
        </Typography>
        <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 6 }}>
          Comprehensive guides and resources for federal data governance
        </Typography>
        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <Card sx={{ p: 3, height: '100%', '&:hover': { boxShadow: 4, cursor: 'pointer' } }}>
              <Typography variant="h6" gutterBottom sx={{ color: '#003366', fontWeight: 600 }}>
                508 Compliance Checklist
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Comprehensive guide to ensuring your data platform meets federal accessibility standards. Includes testing tools, common pitfalls, and implementation best practices.
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{ p: 3, height: '100%', '&:hover': { boxShadow: 4, cursor: 'pointer' } }}>
              <Typography variant="h6" gutterBottom sx={{ color: '#003366', fontWeight: 600 }}>
                DHS Technology Landscape 2025
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Understanding current DHS technology priorities and leadership. Covers CIO Antoine McCord's initiatives, S&T research focus, and strategic opportunities.
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{ p: 3, height: '100%', '&:hover': { boxShadow: 4, cursor: 'pointer' } }}>
              <Typography variant="h6" gutterBottom sx={{ color: '#003366', fontWeight: 600 }}>
                FedRAMP Migration Guide
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Complete roadmap for achieving FedRAMP authorization. Includes migration strategy, cost analysis, timeline, and step-by-step implementation plan.
              </Typography>
            </Card>
          </Grid>
        </Grid>

        <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 600, mt: 6, mb: 3 }}>
          Additional Resources
        </Typography>
        <Grid container spacing={3}>
          {[
            { title: 'Data Governance Best Practices', desc: 'Framework for implementing data governance in federal agencies' },
            { title: 'NIST 800-53 Implementation Guide', desc: 'Practical guide to implementing security controls' },
            { title: 'Cross-Agency Data Sharing Framework', desc: 'Strategies for secure data sharing between agencies' },
            { title: 'AI Governance for Government', desc: 'Responsible AI framework for federal applications' }
          ].map((resource, idx) => (
            <Grid item xs={12} sm={6} key={idx}>
              <Card sx={{ p: 2, '&:hover': { boxShadow: 2, cursor: 'pointer' } }}>
                <Typography variant="subtitle1" sx={{ color: '#003366', fontWeight: 600 }}>{resource.title}</Typography>
                <Typography variant="body2" color="text.secondary">{resource.desc}</Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Newsletter Section */}
      <Box sx={{ bgcolor: '#f5f5f5', py: 8 }}>
        <Container maxWidth="md">
          <Typography variant="h4" align="center" gutterBottom sx={{ fontWeight: 700, color: '#003366', mb: 2 }}>
            Stay Updated on Government Data Governance
          </Typography>
          <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 4 }}>
            Subscribe to receive the latest insights, guides, and updates on federal data governance and compliance.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, maxWidth: 600, mx: 'auto' }}>
            <TextField
              fullWidth
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              sx={{ bgcolor: 'white' }}
            />
            <Button
              variant="contained"
              sx={{
                bgcolor: '#003366',
                color: 'white',
                px: 4,
                whiteSpace: 'nowrap',
                '&:hover': { bgcolor: '#002244' }
              }}
              onClick={handleSubscribe}
              disabled={subscribeLoading}
            >
              Subscribe
            </Button>
          </Box>
          <Box sx={{ mt: 2 }}>
            <div aria-live="polite" aria-atomic="true">
              {subscribeMsg && (
                <span style={{ color: '#2e7d32', fontWeight: 600 }}>{subscribeMsg}</span>
              )}
              {subscribeErr && (
                <span style={{ color: '#b00020', fontWeight: 600 }}>{subscribeErr}</span>
              )}
            </div>
          </Box>
        </Container>
      </Box>

      {/* Why Choose E-Unify Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h3" align="center" gutterBottom sx={{ fontWeight: 700, color: '#003366', mb: 2 }}>
          Why Choose E-Unify?
        </Typography>
        <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 6 }}>
          E-Unify provides a comprehensive platform for managing your organization's data assets with enterprise-grade security, compliance, and governance capabilities.
        </Typography>
        <Grid container spacing={2}>
          {[
            'Unified data governance across all domains',
            'Automated compliance and policy enforcement',
            'Real-time data quality monitoring',
            'Enterprise-grade security and access control',
            'Comprehensive audit trails and reporting',
            'Seamless integration with existing systems'
          ].map((benefit, idx) => (
            <Grid item xs={12} sm={6} key={idx}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#003366' }} />
                <Typography variant="body1">{benefit}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Thought Leadership Section */}
      <Box sx={{ bgcolor: '#f5f5f5', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" align="center" gutterBottom sx={{ fontWeight: 700, color: '#003366', mb: 2 }}>
            Thought Leadership
          </Typography>
          <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 6 }}>
            Insights and guidance for government AI transformation
          </Typography>
          
          <Card sx={{ 
            maxWidth: 900, 
            mx: 'auto',
            borderLeft: '6px solid #B31B1B',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 12px 32px rgba(179,27,27,0.2)'
            }
          }}>
            <Grid container>
              <Grid item xs={12} md={4} sx={{ 
                bgcolor: '#003366', 
                color: 'white',
                p: 4,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center'
              }}>
                <Box sx={{ 
                  fontSize: '4rem',
                  mb: 2
                }}>
                  📚
                </Box>
                <Typography variant="h6" align="center" sx={{ fontWeight: 600, color: 'white' }}>
                  New Book
                </Typography>
                <Typography variant="body2" align="center" sx={{ color: 'rgba(255,255,255,0.9)', mt: 1 }}>
                  By Russell Brian Riehl
                </Typography>
              </Grid>
              <Grid item xs={12} md={8} sx={{ p: 4 }}>
                <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 700 }}>
                  The Straight-Line Approach
                </Typography>
                <Typography variant="subtitle1" gutterBottom sx={{ color: '#B31B1B', fontWeight: 600 }}>
                  Transforming from the Information Age to the AI Era
                </Typography>
                <Typography variant="body2" paragraph sx={{ mt: 2, lineHeight: 1.8 }}>
                  A comprehensive guide for government leaders navigating AI transformation. Learn actionable methodologies, 
                  practical tools, and real-world examples for successfully transitioning your agency into the AI era.
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, mt: 3, flexWrap: 'wrap' }}>
                  <Chip label="AI Strategy" size="small" sx={{ bgcolor: 'rgba(0,51,102,0.1)', color: '#003366' }} />
                  <Chip label="Government Focus" size="small" sx={{ bgcolor: 'rgba(179,27,27,0.1)', color: '#B31B1B' }} />
                  <Chip label="Practical Tools" size="small" sx={{ bgcolor: 'rgba(0,51,102,0.1)', color: '#003366' }} />
                </Box>
                <Box sx={{ mt: 3 }}>
                  <Button
                    variant="contained"
                    onClick={() => navigate('/about')}
                    sx={{
                      bgcolor: '#003366',
                      color: 'white',
                      textTransform: 'none',
                      fontWeight: 600,
                      '&:hover': { bgcolor: '#0071BC' }
                    }}
                  >
                    Learn More
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/contact')}
                    sx={{
                      ml: 2,
                      borderColor: '#B31B1B',
                      color: '#B31B1B',
                      textTransform: 'none',
                      fontWeight: 600,
                      '&:hover': { borderColor: '#8B1515', bgcolor: 'rgba(179,27,27,0.04)' }
                    }}
                  >
                    Request Information
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Card>
        </Container>
      </Box>

      {/* Final CTA Section */}
      <Box sx={{ bgcolor: '#003366', color: 'white', py: 8, textAlign: 'center' }}>
        <Container maxWidth="md">
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, mb: 3, color: 'white' }}>
            Ready to Get Started?
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, fontSize: '1.1rem' }}>
            Join organizations that trust E-Unify for their data governance and unification needs. Experience the power of unified data management.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
              onClick={handleRequestDemo}
              sx={{
                bgcolor: 'white',
                color: '#003366',
                px: 4,
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 600,
                '&:hover': { bgcolor: '#f5f5f5' }
              }}
            >
              Request a Demo →
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={handleSignIn}
              sx={{
                borderColor: 'white',
                color: 'white',
                px: 4,
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 600,
                '&:hover': { borderColor: 'white', bgcolor: 'rgba(255, 255, 255, 0.1)' }
              }}
            >
              Sign In
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Footer */}
      <Box component="footer" sx={{ bgcolor: '#f5f5f5', py: 4, borderTop: '1px solid #ddd' }}>
        <Container maxWidth="lg">
          <Typography variant="body2" color="text.secondary" align="center">
            © {new Date().getFullYear()} E-Unify. All rights reserved.
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
            Section 508 Compliant | WCAG 2.0 AA
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default LandingPage;
